// /account — account centre root (spec 20). Delete-account / data-export entries live
// directly on this root list (Apple 5.1.1(v) one-level reachability — 20 §1 A8 note).
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { hasBrandCap } from '../../lib/brand';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { ThemeSwitcher } from '../../components/ThemeSwitcher';
import { useAuth } from '../../auth/AuthContext';
import { formatTimestampUs } from '../../lib/format';
import ChangePasswordCard from './ChangePasswordCard';
import SharedWithMeCard from './SharedWithMeCard';
import { MyConfirmKeysCard, MyPanelPinCard } from '../devices/PanelP4Cards';
import { MyPanelNamesCard } from '../devices/PanelNameCards';
import VoiceAssistantsCard from './VoiceAssistantsCard';
import DataExportCard from './DataExportCard';
import DeleteAccountCard from './DeleteAccountCard';
import NotificationCard from './NotificationCard';
import AboutCard from './AboutCard';
import DiagnosticsCard from './DiagnosticsCard';

export interface Profile {
  user_id: string;
  email: string;
  display_name: string;
  region: string;
  role: string;
  status: string;
  email_verified: boolean;
  company_id: string | null;
  created_at: number;
  updated_at: number;
}

const REGION_KEY: Record<string, string> = { us: 'account.regionUs', eu: 'account.regionEu', apac: 'account.regionApac', cn: 'account.regionCn' };

export default function AccountPage() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // display_name editing
  const [editing, setEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const [loggingOut, setLoggingOut] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await apiFetch<Profile>('/v3/me');
      setProfile(res.data);
      // hydrate the auth context (user may be null after a silent cookie refresh)
      setUser({ ...(user ?? {}), user_id: res.data.user_id, email: res.data.email, region: res.data.region, role: res.data.role });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ACCOUNT_INACTIVE') {
        // 20 §9 E7: unified handling — clear session, back to login
        await logout();
        return;
      }
      setLoadError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.loadFailedRetry'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout, setUser]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveName = useCallback(async () => {
    const trimmed = nameDraft.trim();
    if (trimmed.length < 1 || trimmed.length > 32) {
      setNameError(t('account.displayNameLength'));
      return;
    }
    setNameBusy(true);
    setNameError(null);
    try {
      // 20 §2.2 / AC-1: PATCH carries display_name ONLY (immutable fields → 400 INVALID_REQUEST)
      const res = await apiFetch<Profile>('/v3/me', { method: 'PATCH', body: { display_name: trimmed } });
      setProfile(res.data);
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'ACCOUNT_INACTIVE') {
        await logout();
        return;
      }
      setNameError(e instanceof ApiError ? `${e.message}（${e.code}）` : t('common.saveFailedRetry'));
    } finally {
      setNameBusy(false);
    }
  }, [nameDraft, logout]);

  return (
    <div className="account-grid">
      <h1 style={{ margin: 0 }}>{t('account.title')}</h1>

      {/* A1 profile */}
      <div className="card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.profile')}</h2>
        {loading && <p className="text-secondary">{t('common.loading')}</p>}
        {!loading && loadError && (
          <div>
            <p className="error-text">{loadError}</p>
            <button className="btn-ghost" onClick={() => void load()}>
              {t('common.retry')}
            </button>
          </div>
        )}
        {!loading && !loadError && profile && (
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 'var(--spacing-4) var(--spacing-8)' }}>
            <dt className="text-secondary">{t('account.displayName')}</dt>
            <dd style={{ margin: 0 }}>
              {editing ? (
                <span style={{ display: 'inline-flex', gap: 'var(--spacing-3)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    maxLength={32}
                    disabled={nameBusy}
                    style={{ width: 220 }}
                  />
                  <button className="btn-primary" disabled={nameBusy} onClick={() => void saveName()}>
                    {nameBusy ? t('common.saving') : t('common.save')}
                  </button>
                  <button className="btn-ghost" disabled={nameBusy} onClick={() => setEditing(false)}>
                    {t('common.cancel')}
                  </button>
                  {nameError && <span className="error-text">{nameError}</span>}
                </span>
              ) : (
                <span style={{ display: 'inline-flex', gap: 'var(--spacing-4)', alignItems: 'center' }}>
                  {profile.display_name || '—'}
                  <button
                    className="btn-ghost"
                    onClick={() => {
                      setNameDraft(profile.display_name ?? '');
                      setNameError(null);
                      setEditing(true);
                    }}
                  >
                    {t('common.edit')}
                  </button>
                </span>
              )}
            </dd>
            <dt className="text-secondary">{t('account.email')}</dt>
            <dd style={{ margin: 0 }}>
              {profile.email}{' '}
              {profile.email_verified && (
                <span style={{ color: 'var(--color-semantic-success)', fontSize: 'var(--font-size-md)' }}>{t('account.verified')}</span>
              )}
              {/* v1: email is read-only — no change-email entry (20 §2.1) */}
            </dd>
            <dt className="text-secondary">{t('account.dataRegion')}</dt>
            <dd style={{ margin: 0 }}>{REGION_KEY[profile.region] ? t(REGION_KEY[profile.region]) : profile.region}</dd>
            <dt className="text-secondary">{t('account.createdAt')}</dt>
            <dd style={{ margin: 0 }}>{formatTimestampUs(profile.created_at)}</dd>
          </dl>
        )}
      </div>

      {/* 本地相簿(APP#42):帳號層總覽入口(全部裝置的本機截圖 / 錄影) */}
      <div className="card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('gallery.title')}</h2>
        <p className="text-secondary" style={{ marginTop: 0 }}>{t('gallery.accountEntryHint')}</p>
        <Link className="btn-ghost" to="/gallery">{t('gallery.open')} →</Link>
      </div>

      {/* shared-with-me (spec 18 §5, IA per 16: Account Tab section) */}
      <SharedWithMeCard />
      <MyPanelPinCard />
      {/* V-11 可以幫面板確認的手機（D2；網頁只看 / 刪，登記在手機 App） */}
      <MyConfirmKeysCard />
      {/* 我怎麼稱呼這些面板（來電顯示的名字，2026-10-22） */}
      <MyPanelNamesCard />

      {/* voice assistants account linking (cs#14) */}
      <VoiceAssistantsCard />

      {/* 偏好:語言(i18n,EPIC APP#15)+ 主題(spec 16 §6.1)。個人偏好收進 Account(16 §IA) */}
      <div className="card stack gap-4">
        <LanguageSwitcher />
        <ThemeSwitcher />
      </div>

      {/* 訂閱與方案（帳號層 membership）— 唯一入口（`19` §66 訂閱收在 Account，APP#145 (a)）。
          brand 生態未授權 subscription:manage 時整卡不渲染（08 §2.1.5），否則點進去是「此生態未提供」死路。 */}
      {hasBrandCap('subscription:manage') && (
        <div className="card">
          <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.membershipsTitle')}</h2>
          <p className="text-secondary" style={{ marginTop: 0 }}>{t('account.membershipsIntro')}</p>
          <Link className="btn-ghost" to="/account/subscription">{t('account.membershipsOpen')} →</Link>
        </div>
      )}

      {/* A2 security */}
      <div className="card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('account.security')}</h2>
        <p style={{ marginTop: 0 }}>
          <Link to="/account/sessions">{t('account.sessions')} →</Link>
        </p>
        <ChangePasswordCard />
      </div>

      {/* A4 通知 / push-token 總開關（spec 20 §8.1） */}
      <NotificationCard />

      {/* A6 關於 / App 版本（spec 20 §8.2；全域 426 由 ForceUpgradeOverlay 處理） */}
      <AboutCard />

      {/* 使用者同意後的 crash / 診斷回報（common/07 diagnostics extension）。 */}
      <DiagnosticsCard />

      {/* APP#50：可搜尋的離線 FAQ + 具裝置／圖片附件的支援單。 */}
      <div className="card">
        <h2 style={{ marginTop: 0, fontSize: 'var(--font-size-xl)' }}>{t('support.title')}</h2>
        <p className="text-secondary">{t('support.accountEntry')}</p>
        <Link className="btn-ghost" to="/account/support">{t('support.open')} →</Link>
      </div>

      {/* A8 account & data (GDPR / store compliance) — one level from root */}
      <DataExportCard />
      <DeleteAccountCard />

      {/* A7 logout */}
      <div className="card">
        <button
          className="btn-danger"
          disabled={loggingOut}
          onClick={() => {
            setLoggingOut(true);
            void logout();
          }}
        >
          {loggingOut ? t('account.loggingOut') : t('account.logout')}
        </button>
      </div>
    </div>
  );
}
